/**
 * Timeline Section: scroll-driven pin, staggered slide transition (text → image → year out, then in from bottom),
 * rail highlight, click-to-slide via ScrollToPlugin, Skip to next section.
 * Uses GSAP + ScrollTrigger + ScrollToPlugin on desktop; Swiper on mobile (<=767px).
 *
 * Viewport split: innerWidth <= MOBILE_BREAKPOINT (767) = mobile Swiper only, no timeline pin;
 * wider = desktop/tablet timeline pin + scroll distance (see pin end in px via innerHeight at refresh).
 */

(function() {
	'use strict';

	let initAttempts = 0;
	let swiperInitAttempts = 0;
	const maxInitAttempts = 50;
	const maxSwiperInitAttempts = 30;
	const MOBILE_BREAKPOINT = 767;
	/** Viewport height units of scroll range per slide while the timeline is pinned (lower = fewer wheel strokes per step). */
	const TIMELINE_SEGMENT_VH = 60;

	// Refs for resize: switch between mobile slider and desktop GSAP
	let timelineMobileSwiper = null;
	let timelineDesktopSt = null;
	let timelineDesktopRefresh = null;
	let currentIsMobile = null;
	/** Last innerWidth seen by timeline resize — used to ignore iOS URL-bar "resize" (height-only). */
	let timelineResizeLastInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

	function isMobile() {
		return window.innerWidth <= MOBILE_BREAKPOINT;
	}

	/**
	 * Scroll the years rail inside its own overflow — never use scrollIntoView on mobile horizontal strip:
	 * that can scroll the document vertically and fight Lenis at page bottom (endless bounce).
	 */
	function scrollTimelineYearsStripIntoView(yearsListEl, activeItemEl, useEndInline) {
		if (!yearsListEl || !activeItemEl) return;
		var list = yearsListEl;
		var el = activeItemEl;
		if (list.scrollWidth > list.clientWidth + 1) {
			var maxScrollX = Math.max(0, list.scrollWidth - list.clientWidth);
			var itemLeftInList = el.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
			var elW = el.offsetWidth;
			var targetX = useEndInline
				? itemLeftInList + elW - list.clientWidth
				: itemLeftInList - (list.clientWidth - elW) / 2;
			targetX = Math.max(0, Math.min(targetX, maxScrollX));
			if (Math.abs(list.scrollLeft - targetX) > 1) {
				list.scrollTo({ left: targetX, behavior: 'auto' });
			}
			return;
		}
		if (list.scrollHeight > list.clientHeight + 1) {
			var listH = list.clientHeight;
			var itemH = el.offsetHeight;
			var targetY = el.offsetTop - listH / 2 + itemH / 2;
			var maxScrollY = Math.max(0, list.scrollHeight - listH);
			list.scrollTop = Math.max(0, Math.min(targetY, maxScrollY));
			return;
		}
		el.scrollIntoView({ inline: useEndInline ? 'end' : 'center', block: 'nearest', behavior: 'auto' });
	}

	// Distance slides move: must be >= visible area so next card truly enters from bottom (not mid-screen)
	let Y_OFF = 920;
	// Visible gap between outgoing and incoming card so next card doesn't ride on previous
	const CARD_GAP = 410;
	// Out: text first, then image, then year (cascading). Ranges span 0–1 so transition uses full segment (no delay).
	const SEG = {
		textOut: [0, 0.824],
		imageOut: [0.235, 1],
		yearOut: [0.36, 0.89],
		textIn: [0, 0.824],
		imageIn: [0.235, 1],
		yearIn: [0.441, 0.971],
	};

	function getSlideParts(slide) {
		const inner = slide.querySelector('.timeline-slide');
		if (!inner) {
			return { text: null, media: null, year: null };
		}
		return {
			text: inner.querySelector('.timeline-slide__text'),
			media: inner.querySelector('.timeline-slide__media'),
			year: inner.querySelector('.timeline-slide__year-overlay'),
		};
	}

	function setPartState(part, y, opacity) {
		if (!part) {
			return;
		}
		gsap.set(part, { y: y, opacity: opacity, visibility: opacity > 0 ? 'visible' : 'hidden' });
	}

	function segmentT(progress, start, end) {
		const len = end - start;
		if (len <= 0) return 0;
		return Math.min(1, Math.max(0, (progress - start) / len));
	}

	// Smooth ease (softer than cubic) so motion feels fluid like Metric
	function easeSmooth(t) {
		return t * t * (3 - 2 * t);
	}

	function applyTransitionProgress(currentParts, nextParts, localProgress, hasYear) {
		const tTextOut = easeSmooth(segmentT(localProgress, SEG.textOut[0], SEG.textOut[1]));
		const tImageOut = easeSmooth(segmentT(localProgress, SEG.imageOut[0], SEG.imageOut[1]));
		const tYearOut = easeSmooth(segmentT(localProgress, SEG.yearOut[0], SEG.yearOut[1]));
		const tTextIn = easeSmooth(segmentT(localProgress, SEG.textIn[0], SEG.textIn[1]));
		const tImageIn = easeSmooth(segmentT(localProgress, SEG.imageIn[0], SEG.imageIn[1]));
		const tYearIn = easeSmooth(segmentT(localProgress, SEG.yearIn[0], SEG.yearIn[1]));

		// Total travel so there's a visible gap between cards (out goes up more, in starts lower)
		var travel = Y_OFF + CARD_GAP;
		// Current slide: out — moves up and fades
		var outOpacity = 1 - Math.max(tTextOut, tImageOut, tYearOut);
		setPartState(currentParts.text, -travel * tTextOut, outOpacity);
		setPartState(currentParts.media, -travel * tImageOut, outOpacity);
		if (hasYear.current) {
			setPartState(currentParts.year, -travel * tYearOut, outOpacity);
		}

		// Next slide: in from below; same travel so gap stays visible between the two
		var nextY = function(t) { return travel * (1 - t); };
		setPartState(nextParts.text, nextY(tTextIn), tTextIn);
		setPartState(nextParts.media, nextY(tImageIn), tImageIn);
		if (hasYear.next) {
			setPartState(nextParts.year, nextY(tYearIn), tYearIn);
		}
	}

	function applyIdleState(parts, y, opacity) {
		setPartState(parts.text, y, opacity);
		setPartState(parts.media, y, opacity);
		setPartState(parts.year, y, opacity);
	}

	function initTimelineMobileSlider() {
		const section = document.getElementById('timeline-section');
		if (!section) return;
		const slidesInner = section.querySelector('.timeline-section__slides-inner');
		const slides = section.querySelectorAll('.timeline-section__slide');
		const yearButtons = section.querySelectorAll('.timeline-section__year-btn');
		const yearItems = section.querySelectorAll('.timeline-section__year-item');
		const yearsList = section.querySelector('.timeline-section__years');
		if (!slidesInner || !(slidesInner instanceof HTMLElement) || slides.length === 0) return;
		if (!document.body.contains(slidesInner)) return;

		if (typeof Swiper === 'undefined') {
			swiperInitAttempts++;
			if (swiperInitAttempts < maxSwiperInitAttempts) {
				setTimeout(initTimelineMobileSlider, 100);
			} else {
				console.error('Timeline mobile: Swiper failed to load.');
			}
			return;
		}
		swiperInitAttempts = 0;

		function doInit() {
			if (slidesInner.offsetWidth === 0) {
				requestAnimationFrame(doInit);
				return;
			}
			runMobileSliderInit();
		}

		function updateYearActive(idx, skipScrollIntoView) {
			yearButtons.forEach(function(btn, i) {
				btn.classList.toggle('is-active', i === idx);
			});
			yearItems.forEach(function(item, i) {
				item.classList.remove('is-active', 'is-active-first', 'prev-is-active');
				if (i === idx) {
					item.classList.add('is-active');
					if (idx === 0) item.classList.add('is-active-first');
				} else if (i === idx - 1) {
					item.classList.add('prev-is-active');
				}
			});
			slides.forEach(function(slide, i) {
				slide.classList.toggle('is-active', i === idx);
				slide.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
			});
			if (skipScrollIntoView) return;
			var activeItem = yearItems[idx];
			if (activeItem && yearsList) {
				var useEndInline = idx >= yearItems.length - 2;
				scrollTimelineYearsStripIntoView(yearsList, activeItem, useEndInline);
			}
		}

		function scrollActiveYearIntoView(activeIndex) {
			var activeItem = yearItems[activeIndex];
			if (!activeItem || !yearsList) return;
			var useEndInline = activeIndex >= yearItems.length - 2;
			scrollTimelineYearsStripIntoView(yearsList, activeItem, useEndInline);
		}

		function syncMobileSlideHeights(swiperInstance) {
			if (window.innerWidth > MOBILE_BREAKPOINT) return;
			var wrapper = slidesInner.querySelector('.timeline-section__slides-wrapper');
			if (!wrapper) return;
			slidesInner.style.height = 'auto';
			slides.forEach(function(s) {
				if (s && s.style) {
					s.style.height = '';
				}
			});
			wrapper.style.alignItems = 'stretch';
			if (swiperInstance && !swiperInstance.destroyed) {
				swiperInstance.update();
				if (typeof swiperInstance.updateSize === 'function') {
					swiperInstance.updateSize();
				}
			}
		}
		window._timelineSyncMobileHeights = syncMobileSlideHeights;

		function runMobileSliderInit() {
			if (timelineMobileSwiper && timelineMobileSwiper.destroy) {
				timelineMobileSwiper.destroy(true, true);
				timelineMobileSwiper = null;
			}

			if (typeof gsap !== 'undefined') {
				slides.forEach(function(slide) {
					gsap.set(slide, { clearProps: 'all' });
					var parts = getSlideParts(slide);
					[parts.text, parts.media, parts.year].forEach(function(part) {
						if (part) gsap.set(part, { clearProps: 'all' });
					});
				});
			}

			function initSwiperNow() {
				var sectionWidth = section.getBoundingClientRect().width;
				var cw = Math.min(sectionWidth, window.innerWidth - 32) || Math.max(320, window.innerWidth - 32);
				slidesInner.style.width = cw + 'px';
				slidesInner.style.maxWidth = cw + 'px';
				slidesInner.style.flex = '0 0 ' + cw + 'px';
				slidesInner.style.boxSizing = 'border-box';
				void slidesInner.offsetHeight; // force reflow so Swiper reads correct width

				var swiper;
				try {
					swiper = new Swiper(slidesInner, {
						slideClass: 'timeline-section__slide',
						wrapperClass: 'timeline-section__slides-wrapper',
						slidesPerView: 1,
						spaceBetween: 16,
						autoHeight: true,
						loop: false,
						speed: 600,
						grabCursor: true,
						allowTouchMove: true,
						on: {
							init: function() {
								var self = this;
								var containerW = self.width;
								if (!containerW || containerW > 1e6) {
									containerW = slidesInner.getBoundingClientRect().width || cw;
								}
								var slideW = Math.max(1, containerW - 16);
								self.slides.forEach(function(slideEl, i) {
									if (slideEl && slideEl.style) {
										slideEl.style.width = slideW + 'px';
										if (i === self.slides.length - 1) {
											slideEl.style.marginRight = '0';
										}
									}
								});
								self.updateSize();
								updateYearActive(self.activeIndex, true);
								syncMobileSlideHeights(self);
							},
							slideChange: function() {
								// Only update active state; scroll list after transition so it moves with slides
								updateYearActive(this.activeIndex, true);
							},
							slideChangeTransitionEnd: function() {
								// Re-apply container width so inner doesn't shift; single updateSize after layout (was double — extra reflow).
								var sectionWidth = section.getBoundingClientRect().width;
								var cw = Math.min(sectionWidth, window.innerWidth - 32) || Math.max(320, window.innerWidth - 32);
								slidesInner.style.width = cw + 'px';
								slidesInner.style.maxWidth = cw + 'px';
								slidesInner.style.flex = '0 0 ' + cw + 'px';
								var self = this;
								requestAnimationFrame(function() {
									self.updateSize();
									scrollActiveYearIntoView(self.activeIndex);
								});
							},
						},
					});
				} catch (err) {
					console.error('Timeline mobile Swiper init failed:', err);
					slidesInner.style.width = '';
					slidesInner.style.maxWidth = '';
					slidesInner.style.flex = '';
					return;
				}

				yearButtons.forEach(function(btn) {
					btn.addEventListener('click', function() {
						var idx = parseInt(btn.getAttribute('data-slide-index'), 10);
						if (!isNaN(idx) && swiper && !swiper.destroyed) {
							swiper.slideTo(idx);
						}
					});
				});

				timelineMobileSwiper = swiper;
			}

			if (slidesInner.offsetWidth === 0) {
				requestAnimationFrame(initSwiperNow);
			} else {
				initSwiperNow();
			}
		}

		if (slidesInner.offsetWidth === 0) {
			requestAnimationFrame(doInit);
		} else {
			doInit();
		}
	}

	function initTimelineDesktop() {
		if (typeof gsap === 'undefined') return;

		let ScrollTriggerPlugin = null;
		if (gsap.ScrollTrigger) {
			ScrollTriggerPlugin = gsap.ScrollTrigger;
		} else if (typeof window !== 'undefined' && window.ScrollTrigger) {
			ScrollTriggerPlugin = window.ScrollTrigger;
			gsap.registerPlugin(ScrollTriggerPlugin);
		} else {
			return;
		}

		let ScrollToPluginRef = null;
		if (gsap.ScrollToPlugin) {
			ScrollToPluginRef = gsap.ScrollToPlugin;
		} else if (typeof window !== 'undefined' && window.ScrollToPlugin) {
			ScrollToPluginRef = window.ScrollToPlugin;
		}
		if (ScrollToPluginRef) {
			gsap.registerPlugin(ScrollToPluginRef);
		}

		const section = document.getElementById('timeline-section');
		if (!section) return;

		const slides = section.querySelectorAll('.timeline-section__slide');
		const yearButtons = section.querySelectorAll('.timeline-section__year-btn');
		const yearItems = section.querySelectorAll('.timeline-section__year-item');
		const yearsList = section.querySelector('.timeline-section__years');
		const rail = section.querySelector('.timeline-section__rail');
		const skipBtn = section.querySelector('.timeline-section__skip');

		const N = slides.length;
		if (N === 0 || !rail) {
			return;
		}

		// Ensure next card starts fully below visible area (slide max-height ~619px, use section height)
		Y_OFF = Math.max(720, section.offsetHeight * 0.85, 620);

		const slidePartsList = [];
		slides.forEach(function(slide) {
			slidePartsList.push(getSlideParts(slide));
		});

		var lastScrolledIndex = -1;

		// Display progress is tweened to target so the card always lands fully in place (no half-states)
		var proxy = { displayProgress: 0 };
		var lastTargetProgress = 0;
		var progressTween = null;

		function updateFromProgress(progress) {
			var p = Math.max(0, Math.min(1, progress));
			var baseIndex = Math.min(Math.floor(p * N), N - 1);
			var segmentProgress = p * N - baseIndex;
			var localProgress = Math.min(1, segmentProgress);

			// Only switch to next year when past 50% of transition so the year label matches the visible card
			var activeIndex = segmentProgress >= 0.5 && baseIndex < N - 1 ? baseIndex + 1 : baseIndex;

			yearButtons.forEach(function(btn, i) {
				btn.classList.toggle('is-active', i === activeIndex);
			});
			yearItems.forEach(function(item, i) {
				item.classList.remove('is-active', 'is-active-first', 'prev-is-active');
			});
			if (yearItems[activeIndex]) {
				yearItems[activeIndex].classList.add('is-active');
				if (activeIndex === 0) {
					yearItems[activeIndex].classList.add('is-active-first');
				}
			}
			if (activeIndex >= 1 && yearItems[activeIndex - 1]) {
				yearItems[activeIndex - 1].classList.add('prev-is-active');
			}

			var activeItem = yearItems[activeIndex];
			var isHidden = activeItem && activeItem.classList.contains('timeline-section__year-item--hidden');
			if (activeIndex !== lastScrolledIndex && yearsList && activeItem && !isHidden) {
				lastScrolledIndex = activeIndex;
				var useEndInline = activeIndex >= yearItems.length - 2;
				scrollTimelineYearsStripIntoView(yearsList, activeItem, useEndInline);
			}

			if (localProgress <= 0) {
				slides.forEach(function(slide, i) {
					var visible = i === baseIndex;
					gsap.set(slide, {
						visibility: visible ? 'visible' : 'hidden',
						pointerEvents: visible ? 'auto' : 'none',
					});
					slide.classList.toggle('is-active', visible);
					var parts = slidePartsList[i];
					if (visible) {
						applyIdleState(parts, 0, 1);
					} else {
						applyIdleState(parts, Y_OFF + CARD_GAP, 0);
					}
				});
			} else if (baseIndex < N - 1) {
				var currentParts = slidePartsList[baseIndex];
				var nextParts = slidePartsList[baseIndex + 1];
				var hasYear = {
					current: !!currentParts.year,
					next: !!nextParts.year,
				};

				slides.forEach(function(slide, i) {
					var visible = i === baseIndex || i === baseIndex + 1;
					gsap.set(slide, {
						visibility: visible ? 'visible' : 'hidden',
						pointerEvents: i === baseIndex + 1 ? 'auto' : 'none',
						zIndex: i === baseIndex ? 0 : (i === baseIndex + 1 ? 1 : -1),
					});
					slide.classList.toggle('is-active', i === baseIndex + 1);
					if (i !== baseIndex && i !== baseIndex + 1) {
						applyIdleState(slidePartsList[i], Y_OFF + CARD_GAP, 0);
					}
				});

				applyTransitionProgress(currentParts, nextParts, localProgress, hasYear);
			} else {
				slides.forEach(function(slide, i) {
					var visible = i === N - 1;
					gsap.set(slide, {
						visibility: visible ? 'visible' : 'hidden',
						pointerEvents: visible ? 'auto' : 'none',
					});
					slide.classList.toggle('is-active', visible);
					var parts = slidePartsList[i];
					if (visible) {
						applyIdleState(parts, 0, 1);
					} else {
						applyIdleState(parts, Y_OFF + CARD_GAP, 0);
					}
				});
			}
		}

		// Initial state: first slide fully visible
		slides.forEach(function(slide, i) {
			slide.classList.remove('timeline-section__slide--initial-active');
			gsap.set(slide, { opacity: 1 });
		});
		updateFromProgress(0);

		// Scroll distance expressed in "vh" as a number, but pin `end` is converted to px at each refresh
		// so pin-spacer height does not track CSS vh string (reduces layout jump with Lenis / iOS).
		const scrollDistanceVh = N * TIMELINE_SEGMENT_VH;

		timelineDesktopSt = (ScrollTriggerPlugin.create || ScrollTriggerPlugin)({
			trigger: section,
			start: 'top top',
			end: function() {
				var ih = window.innerHeight || 800;
				return '+=' + (scrollDistanceVh * ih / 100);
			},
			pin: true,
			anticipatePin: 0,
			// When scrolling up into the pin from below: sync card display to scroll progress (mobile).
			onEnterBack: function(self) {
				if (isMobile()) {
					var p = self.progress;
					var targetProgress = Math.max(0, Math.min(1, Math.round(p * N) / N));
					proxy.displayProgress = targetProgress;
					lastTargetProgress = targetProgress;
					if (progressTween) {
						progressTween.kill();
						progressTween = null;
					}
					updateFromProgress(proxy.displayProgress);
				}
			},
			onUpdate: function(self) {
				if (progressTween) return;
				var p = self.progress;
				var targetStep = Math.round(p * N);
				var lastStep = Math.round(lastTargetProgress * N);
				var stepDiff = targetStep - lastStep;
				if (Math.abs(stepDiff) > 1) {
					targetStep = lastStep + (stepDiff > 0 ? 1 : -1);
				}
				var targetProgress = Math.max(0, Math.min(1, targetStep / N));

				if (targetProgress !== lastTargetProgress) {
					lastTargetProgress = targetProgress;
					var duration = (self.direction === -1) ? 0.2 : 0.4;
					progressTween = gsap.to(proxy, {
						displayProgress: targetProgress,
						duration: duration,
						ease: 'power2.inOut',
						overwrite: true,
						onUpdate: function() {
							updateFromProgress(proxy.displayProgress);
						},
						onComplete: function() {
							progressTween = null;
						},
					});
				}
			},
		});

		// Click year -> scroll to slide (use Lenis when available so mobile stays in sync)
		// Use (idx - 0.5)/N for idx > 0 so we land in the segment (idx-1, idx) with activeIndex = idx,
		// avoiding rounding that would highlight idx+1 when scrolling to idx/N.
		var lenisRef = typeof window.evonaLenis !== 'undefined' ? window.evonaLenis : null;
		yearButtons.forEach(function(btn) {
			btn.addEventListener('click', function() {
				const idx = parseInt(btn.getAttribute('data-slide-index'), 10);
				if (isNaN(idx) || idx < 0 || idx >= N) {
					return;
				}
				const scrollToProgress = idx === 0 ? 0 : (idx - 0.5) / N;
				const start = timelineDesktopSt.start;
				const end = timelineDesktopSt.end;
				const y = start + (end - start) * scrollToProgress;
				if (lenisRef && typeof lenisRef.scrollTo === 'function') {
					lenisRef.scrollTo(y, { duration: 0.8 });
				} else if (ScrollToPluginRef) {
					gsap.to(window, {
						scrollTo: { y: y, autoKill: false },
						duration: 0.8,
						ease: 'power2.inOut',
						overwrite: 'auto',
					});
				} else {
					window.scrollTo({ top: y, behavior: 'smooth' });
				}
			});
		});

		// Skip: scroll to end of timeline, then to next section (always use data-timeline-next / #solution-section, not DOM order)
		if (skipBtn) {
			skipBtn.addEventListener('click', function() {
				const nextSel = section.getAttribute('data-timeline-next') || '#solution-section';
				const nextEl = document.querySelector(nextSel);

				const lenis = typeof window.evonaLenis !== 'undefined' ? window.evonaLenis : null;
				const endY = timelineDesktopSt.end;

				function scrollToY(y, duration, then) {
					if (lenis && typeof lenis.scrollTo === 'function') {
						lenis.scrollTo(y, {
							duration: duration,
							immediate: false,
							onComplete: then || undefined,
						});
					} else if (ScrollToPluginRef) {
						gsap.to(window, {
							scrollTo: { y: y, autoKill: false },
							duration: duration,
							ease: 'power2.inOut',
							overwrite: 'auto',
							onComplete: then,
						});
					} else {
						window.scrollTo({ top: y, behavior: 'smooth' });
						if (then) {
							setTimeout(then, 400);
						}
					}
				}

				if (lenis && nextEl && typeof lenis.scrollTo === 'function') {
					// Use Lenis: scroll to end of timeline then to next section (by element so Lenis resolves position)
					lenis.scrollTo(endY, {
						duration: 0.6,
						onComplete: function() {
							lenis.scrollTo(nextEl, { duration: 0.5 });
						},
					});
				} else {
					scrollToY(endY, 0.6, function() {
						if (nextEl) {
							const nextY = nextEl.getBoundingClientRect().top + window.pageYOffset;
							scrollToY(nextY, 0.5);
						}
					});
				}
			});
		}

		function refreshAndSyncTimeline() {
			Y_OFF = Math.max(720, section.offsetHeight * 0.85, 620);
			if (timelineDesktopSt && typeof timelineDesktopSt.refresh === 'function') {
				timelineDesktopSt.refresh();
			} else {
				ScrollTriggerPlugin.refresh();
			}
			var p = timelineDesktopSt.progress;
			var targetProgress = Math.round(p * N) / N;
			targetProgress = Math.max(0, Math.min(1, targetProgress));
			proxy.displayProgress = targetProgress;
			lastTargetProgress = targetProgress;
			if (progressTween) {
				progressTween.kill();
				progressTween = null;
			}
			updateFromProgress(proxy.displayProgress);
			if (yearsList) {
				lastScrolledIndex = -1;
				var activeItem = section.querySelector('.timeline-section__year-item.is-active');
				if (activeItem) {
					var yearItemsArr = section.querySelectorAll('.timeline-section__year-item');
					var activeIdx = Array.prototype.indexOf.call(yearItemsArr, activeItem);
					var useEndInline = yearItemsArr.length && activeIdx >= yearItemsArr.length - 2;
					scrollTimelineYearsStripIntoView(yearsList, activeItem, useEndInline);
				}
			}
		}

		timelineDesktopRefresh = refreshAndSyncTimeline;

		// Mobile: when scrolling up from below, "catch" into Timeline pin only in a narrow band just above pin end.
		// Disabled on touch devices to avoid jitter (programmatic scrollTo conflicts with finger scroll); Lenis on mobile smooths scroll instead.
		if (isMobile() && !('ontouchstart' in window)) {
			var lastScrollY = 0;
			var catchCooldown = 0;
			window.addEventListener('scroll', function() {
				var scrollY = window.pageYOffset || window.scrollY;
				var vh = window.innerHeight || 800;
				if (scrollY >= lastScrollY) { lastScrollY = scrollY; return; }
				if (catchCooldown > Date.now()) { lastScrollY = scrollY; return; }
				var start = timelineDesktopSt.start;
				var end = timelineDesktopSt.end;
				if (scrollY > end && scrollY - end < vh * 0.6) {
					catchCooldown = Date.now() + 1200;
					requestAnimationFrame(function() {
						window.scrollTo(0, Math.max(start, end - Math.min(vh * 0.35, 180)));
					});
				}
				lastScrollY = scrollY;
			}, { passive: true });
		}
	}

	let timelineResizeBound = false;
	function handleTimelineResize() {
		const w = window.innerWidth;
		const nowMobile = isMobile();
		if (currentIsMobile !== nowMobile) {
			timelineResizeLastInnerWidth = w;
			if (nowMobile) {
				if (timelineDesktopSt) {
					timelineDesktopSt.kill();
					timelineDesktopSt = null;
				}
				timelineDesktopRefresh = null;
				currentIsMobile = true;
				initTimelineMobileSlider();
			} else {
				if (timelineMobileSwiper && timelineMobileSwiper.destroy) {
					timelineMobileSwiper.destroy(true, true);
					timelineMobileSwiper = null;
				}
				var section = document.getElementById('timeline-section');
				var slidesInnerEl = section && section.querySelector('.timeline-section__slides-inner');
				if (slidesInnerEl) {
					slidesInnerEl.style.width = '';
					slidesInnerEl.style.maxWidth = '';
					slidesInnerEl.style.flex = '';
					slidesInnerEl.style.height = '';
				}
				currentIsMobile = false;
				initTimelineDesktop();
			}
		} else if (!currentIsMobile && timelineDesktopRefresh) {
			timelineResizeLastInnerWidth = w;
			clearTimeout(window._timelineResizeTimer);
			window._timelineResizeTimer = setTimeout(timelineDesktopRefresh, 250);
		} else if (currentIsMobile && typeof window._timelineSyncMobileHeights === 'function' && timelineMobileSwiper && !timelineMobileSwiper.destroyed) {
			if (w === timelineResizeLastInnerWidth) {
				return;
			}
			timelineResizeLastInnerWidth = w;
			clearTimeout(window._timelineResizeTimer);
			window._timelineResizeTimer = setTimeout(function() {
				window._timelineSyncMobileHeights(timelineMobileSwiper);
			}, 100);
		}
	}

	function setupTimelineResize() {
		if (timelineResizeBound) return;
		timelineResizeBound = true;
		window.addEventListener('resize', function() {
			clearTimeout(window._timelineResizeTimer);
			window._timelineResizeTimer = setTimeout(handleTimelineResize, 100);
		});
	}

	function initTimelineAnimation() {
		const section = document.getElementById('timeline-section');
		if (!section) return;

		if (isMobile()) {
			currentIsMobile = true;
			initTimelineMobileSlider();
			setupTimelineResize();
			return;
		}

		currentIsMobile = false;
		if (typeof gsap === 'undefined') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initTimelineAnimation, 100);
			} else {
				console.error('Timeline: GSAP failed to load.');
			}
			return;
		}
		initTimelineDesktop();
		setupTimelineResize();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTimelineAnimation);
	} else {
		initTimelineAnimation();
	}
})();
